import { useBundles, useCreateBundle, useDeleteBundle, useEditBundle } from '@/services/api/hooks/bundles';
import { useCompany } from '@/services/api/hooks/companies';
import { PropsWithChildren, useMemo } from 'react';
import { toast } from 'sonner';
import { create } from 'zustand';
import { BaseBundleArgs, BundleContext, BundleService, EditBundleArgs, getBundleState } from './useBundleService';

interface BundlerProviderProps {
  mspId: string;
  customerId: string;
}

export function BundleProvider({ mspId, customerId, children }: PropsWithChildren<BundlerProviderProps>) {
  const [bundleResponse] = useBundles(mspId, customerId);
  const [customer] = useCompany(mspId, customerId);
  const createBundle = useCreateBundle();
  const editBundle = useEditBundle();
  const deleteBundle = useDeleteBundle();

  const store = useMemo(
    () =>
      create<BundleService>(() => ({
        bundleState: getBundleState(customer),
        bundles: bundleResponse,
        getBundleById: (id: string) => {
          const bundle = bundleResponse.find(bundle => bundle.id === id);

          if (!bundle) return null;

          return bundle;
        },
        createBundle: async (data: BaseBundleArgs) => {
          const payload = {
            ...data,
            mspId,
            customerId,
            description: ''
          };

          await createBundle.mutateAsync(payload);
          toast.success('Successfully created bundle');
        },
        editBundle: async (data: EditBundleArgs) => {
          await editBundle.mutateAsync({
            ...data,
            mspId,
            customerId,
            description: ''
          });
          toast.success('Successfully edited bundle');
        },
        deleteBundle: async (id: string) => {
          await deleteBundle.mutateAsync({
            id,
            mspId,
            customerId
          });
          toast.success('Successfully deleted bundle');
        }
      })),
    [bundleResponse, customer]
  );

  return <BundleContext.Provider value={store}>{children}</BundleContext.Provider>;
}
